import { IProductSideNav } from '@adaptiva/core/app-shell';
import { Licensing } from '@adaptiva/core/licensing';
import { Prereq } from '@adaptiva/core/prereq';
import { EBuiltInIds, EJavaClassNames, GlobalPermissions, hasGlobalUiPermissionPrereq, hasObjectReadPermissionsPrereq, hasSomeProductsPrereq, hasVisibleObjectPrereq, serverMetadataFlagPrereq } from '@adaptiva/data-service';

const _baseUrl: string = '/settings';

export const SETTINGS_NAVIGATION: IProductSideNav = {
	productName: 'Settings',
	sections: [
		{
			items: [
				{
					display: 'Adaptiva Content Publication',
					route: `${_baseUrl}/cloud-content`,
					prerequisites: [
						Prereq.not(serverMetadataFlagPrereq('cloudTenant')),
						hasVisibleObjectPrereq(EJavaClassNames.CloudContentSettings),
					]
				},
				{
					display: 'Client Authorization',
					route: `${_baseUrl}/client-auth`,
					prerequisites: [
						Prereq.not(serverMetadataFlagPrereq('cloudTenant')),
						hasVisibleObjectPrereq(EJavaClassNames.ClientAuthObject),
					]
				},
				{
					display: 'Client Trusted Certificates',
					prerequisites: [
						Prereq.or(
							hasVisibleObjectPrereq(EJavaClassNames.ClientTrustedCertificatePolicy),
							hasVisibleObjectPrereq(EJavaClassNames.ClientTrustedCertificate)
						)
					],
					children: [
						{
							display: 'Policies',
							route: `${_baseUrl}/client-trusted-certificates/policies`,
							linkedRoutes: [`${_baseUrl}/client-trusted-certificates/policies/edit/:id`],
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.ClientTrustedCertificatePolicy)]
						},
						{
							display: 'Certificates',
							route: `${_baseUrl}/client-trusted-certificates/certs`,
							linkedRoutes: [`${_baseUrl}/client-trusted-certificates/certs/edit/:id`],
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.ClientTrustedCertificate)]
						},
					]
				},
				{
					display: 'Client Upgrade',
					route: `${_baseUrl}/client-upgrade`,
					prerequisites: [hasObjectReadPermissionsPrereq(EBuiltInIds.CLIENT_UPGRADE_SETTINGS_OBJECT_ID)],
				},
				{
					display: 'Legacy Client Upgrade (Windows)',
					route: `${_baseUrl}/client-auto-upgrade`,
					prerequisites: [
						Prereq.not(serverMetadataFlagPrereq('cloudTenant')),
						hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_CLIENT_AUTO_UPGRADE)
					],
				},
				{
					display: 'Client Downloads',
					route: `${_baseUrl}/client-downloads`,
					prerequisites: [serverMetadataFlagPrereq('cloudTenant')],
				},
				{
					display: 'Client Settings Policies',
					route: `${_baseUrl}/client-settings`,
					linkedRoutes: [`${_baseUrl}/client-settings/edit/:id`],
					prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.ClientSettingsServerPolicy)],
				},
				{
					display: 'Cloud Storage',
					route: `${_baseUrl}/cloud-storage`,
					prerequisites: [
						Prereq.not(serverMetadataFlagPrereq('cloudTenant')),
						hasVisibleObjectPrereq(EJavaClassNames.AzureBlobStoreSettings)
					],
				},
				{
					display: 'SMTP Settings',
					route: `${_baseUrl}/smtp`,
					prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.EventNotificationSettings)],
				},
				{
					display: 'Manual Policy Sync',
					route: `${_baseUrl}/manual-policy-sync`,
					prerequisites: [
						hasSomeProductsPrereq(...Licensing.getEnterpriseProducts()),
						Prereq.not(serverMetadataFlagPrereq('cloudPortal')),
					],
				},
				{
					display: 'REST API Endpoints',
					children: [
						{
							display: 'Endpoints',
							route: `${_baseUrl}/rest-api/endpoints`,
							linkedRoutes: [`${_baseUrl}/rest-api/endpoints/edit/:id`],
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.RestApiEndpoint)],
						},
						{
							display: 'Configuration',
							route: `${_baseUrl}/rest-api/configuration`,
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.RestApiEndpointGlobalConfig)],
						},
					],
					prerequisites: [
						hasSomeProductsPrereq(...Licensing.getEnterpriseProducts()),
						Prereq.or(
							hasVisibleObjectPrereq(EJavaClassNames.RestApiEndpoint),
							hasVisibleObjectPrereq(EJavaClassNames.RestApiEndpointGlobalConfig),
						)
					],
				},
				{
					display: 'Security',
					children: [
						{
							display: 'Administrators',
							route: `${_baseUrl}/security/administrators`,
							linkedRoutes: [
								`${_baseUrl}/security/administrators/edit/:id`,
								`${_baseUrl}/security/administrators/oidc-certificate-users/edit/:id`
							],
						},
						{
							display: 'Roles',
							route: `${_baseUrl}/security/roles`,
							linkedRoutes: [`${_baseUrl}/security/roles/edit/:id`],
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.Role)],
						},
						{
							display: 'OIDC Providers',
							route: `${_baseUrl}/security/oidc-providers`,
							linkedRoutes: [`${_baseUrl}/security/oidc-providers/edit/:id`],
							prerequisites: [
								hasVisibleObjectPrereq(EJavaClassNames.OidcProvider),
								Prereq.not(serverMetadataFlagPrereq('cloudTenant')),
							],
						},
						{
							display: 'SAML Providers',
							route: `${_baseUrl}/security/saml-providers`,
							linkedRoutes: [`${_baseUrl}/security/saml-providers/edit/:id`],
							prerequisites: [
								hasVisibleObjectPrereq(EJavaClassNames.SamlProvider),
								Prereq.not(serverMetadataFlagPrereq('cloudTenant')),
							],
						},
						{
							display: 'Class Permissions',
							route: `${_baseUrl}/security/class-permissions`,
							linkedRoutes: [`${_baseUrl}/security/class-permissions/edit/:id`],
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.ClassLevelPermissions)],
						},
						{
							display: 'Permissions Viewer',
							route: `${_baseUrl}/security/permissions-viewer`,
							prerequisites: [hasVisibleObjectPrereq(EJavaClassNames.ClassLevelPermissions)],
						},
						{
							display: 'Authentication Settings',
							route: `${_baseUrl}/security/auth-settings`,
							prerequisites: [hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_ACCESS_SYSTEM_CONFIG_OR_REGISTRY)],
						},
					]
				},
				{
					display: 'Sensor Offline Cache',
					route: `${_baseUrl}/sensor-offline-cache`,
					prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.INVENTORY)],
				},
				{
					display: 'Server Activation',
					route: `${_baseUrl}/server-activation`,
					prerequisites: [hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_VIEW_SERVER_ACTIVATION_STATE)],
				}
			]
		},
		{
			title: 'About',
			items: [
				{
					display: 'About',
					route: `${_baseUrl}/about`,
					prerequisites: [hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_VIEW_SERVER_INFO)],
				}
			]
		}
	]
};
